import { useEffect, useRef, useState } from 'react';
import { isComboMilestone } from '@/pages/dsst/dsstConfig';

interface ComboBadgeProps {
  combo: number;
}

// 연속 정답(Combo) 배지 — 3연속부터 노출되는 Purple Filled Badge
// 역할: 숫자가 가장 강한 시각 요소, '연속'은 보조 텍스트
// 모션: 3연속 Pop-in / 일반 증가 숫자 Pop / 마일스톤(5·10·15...) 배지 강화 / 오답 Fade-out
export default function ComboBadge({ combo }: ComboBadgeProps) {
  const prevComboRef = useRef(0);
  const [leavingValue, setLeavingValue] = useState<number | null>(null);

  useEffect(() => {
    const prev = prevComboRef.current;
    prevComboRef.current = combo;

    // 콤보가 리셋(0)되면서 이전 값이 3 이상이면 짧게 Fade-out 처리
    if (combo === 0 && prev >= 3) {
      setLeavingValue(prev);
      const t = window.setTimeout(() => setLeavingValue(null), 260);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [combo]);

  // 오답: 이전 콤보 값으로 짧게 Scale-down + Fade-out
  if (leavingValue !== null && combo === 0) {
    return (
      <div className="shrink-0 flex justify-end items-center h-9 mb-2">
        <span className="inline-flex items-center gap-1.5 pl-3 pr-2.5 py-1.5 rounded-full bg-primary-600 text-white whitespace-nowrap animate-combo-badge-leave">
          <span className="text-xl font-extrabold tabular-nums leading-none">
            {leavingValue}
          </span>
          <span className="text-xs font-semibold leading-none opacity-90">연속</span>
        </span>
      </div>
    );
  }

  // 3연속 미만: 배지 미노출 (자리만 확보해 레이아웃 밀림 방지)
  if (combo < 3) {
    return <div className="shrink-0 h-9 mb-2" />;
  }

  const isFirstAppear = combo === 3;
  const isMilestone = !isFirstAppear && isComboMilestone(combo);

  const badgeAnimation = isFirstAppear
    ? 'animate-combo-badge-pop-in'
    : isMilestone
      ? 'animate-combo-badge-milestone'
      : '';

  const numberAnimation =
    !isFirstAppear && !isMilestone ? 'animate-combo-number-pop' : '';

  return (
    <div className="shrink-0 flex justify-end items-center h-9 mb-2">
      <span
        className={`inline-flex items-center gap-1.5 pl-3 pr-2.5 py-1.5 rounded-full bg-primary-600 text-white whitespace-nowrap ${badgeAnimation}`}
      >
        <span
          key={combo}
          className={`text-xl font-extrabold tabular-nums leading-none ${numberAnimation}`}
        >
          {combo}
        </span>
        <span className="text-xs font-semibold leading-none opacity-90">연속</span>
      </span>
    </div>
  );
}